import { CartItem } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';

export const CROSS_SELL_MARKER = '_CROSS_SELL_';

export interface RequiredCrossSellViolation {
  /** The product line id of the portfolio added to the cart */
  addedProductLineId: ProductLineId;
  /** The name of the portfolio added to the cart */
  addedPortfolioName: string;
  /**
   * The ID of the portfolio that must be added to the cart together with the added portfolio.
   * The part of the ID before the `CROSS_SELL_MARKER` is the same in the added and required portfolios
   */
  requiredPortfolioId: string;
  /** The product line id of the required portfolio that must be added to the cart */
  requiredProductLineId: ProductLineId;
  /**
   * The name of the required portfolio that must be added to the cart
   * Populated by additional helper
   * */
  requiredPortfolioName?: string;
}

function parseCrossSellPortfolioId(portfolioId: string): {
  portfolioPrefix: string;
  requiredProductLineKey: string;
} | null {
  const [portfolioPrefix, requiredProductLineKey] = portfolioId.split(CROSS_SELL_MARKER);

  if (!portfolioPrefix || !requiredProductLineKey) {
    return null;
  }

  return {
    portfolioPrefix,
    requiredProductLineKey,
  };
}

export function getRequiredCrossSellProductLineId(portfolioId: string): ProductLineId | undefined {
  const crossSellPortfolioData = parseCrossSellPortfolioId(portfolioId);

  // Portfolio does not participate in cross-sell validation.
  if (!crossSellPortfolioData) {
    return undefined;
  }

  // Find the ProductLineId corresponding to the product line encoded in the portfolio ID suffix.
  return (Object.keys(PRODUCT_LINES_CONTENT_MAP) as ProductLineId[]).find(
    (productLineId) =>
      PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel.toUpperCase() ===
      crossSellPortfolioData.requiredProductLineKey.toUpperCase(),
  );
}

/**
 *  Build the counterpart portfolio ID using the current portfolio's product line.
 *  Cross-sell portfolio IDs are mirrored and keep the same prefix/casing as the added portfolio.
 *
 *  Dental portfolio: 2026_PORT_123_Focus_CROSS_SELL_VISION
 *
 *  Vision portfolio: 2026_PORT_123_Focus_CROSS_SELL_DENTAL
 */
function buildCounterpartPortfolioId(originalPortfolioId: string, productLineId: ProductLineId): string {
  const [portfolioPrefix, originalProductLineKey] = originalPortfolioId.split(CROSS_SELL_MARKER);

  if (!portfolioPrefix || !originalProductLineKey) {
    return originalPortfolioId;
  }

  const counterpartProductLineKey =
    originalProductLineKey === originalProductLineKey.toUpperCase()
      ? PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel.toUpperCase()
      : PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel;

  return `${portfolioPrefix}${CROSS_SELL_MARKER}${counterpartProductLineKey}`;
}

/**
 * Returns required cross-sell violations when a paired portfolio is missing from the cart.
 */
export function getRequiredCrossSellViolations(cartItems: CartItem[]): RequiredCrossSellViolation[] {
  const cartPortfolioIds = new Set(cartItems.map(({ portfolioId }) => portfolioId.toUpperCase()));

  return cartItems.reduce<RequiredCrossSellViolation[]>((violations, item) => {
    const requiredProductLineId = getRequiredCrossSellProductLineId(item.portfolioId);

    // Ignore invalid cross-sell identifiers that reference an unknown product line.
    if (!requiredProductLineId) {
      return violations;
    }

    const requiredPortfolioId = buildCounterpartPortfolioId(item.portfolioId, item.productLineId);

    // Cross-sell requirement is satisfied.
    if (cartPortfolioIds.has(requiredPortfolioId.toUpperCase())) {
      return violations;
    }

    // Required portfolio is missing from the cart.
    violations.push({
      addedPortfolioName: item.portfolioName,
      addedProductLineId: item.productLineId,
      requiredPortfolioId,
      requiredProductLineId,
    });

    return violations;
  }, []);
}
